import { toRef, MaybeRefOrGetter } from "@vueuse/core";
import { Ref, watch, computed } from "vue";
import {
  RfpLocation,
  RfpCore,
  RfpPhase,
  RfpPhaseResponse,
  RFI,
  QA,
} from "@/types/Marketplace";
import { aggValue } from "@/plugins/Formatting.js";
import { useMD } from "@/lib/useMD";
import { useAsyncState } from "@vueuse/core";

import { getClient, convertNullsToUndefined } from "./client";

import {
  AIProposalSummary,
  AIProposalSummaryAPIResponse,
} from "@/lib/useProposal";

const FINANCING_PREFERENCES: { [key: string]: string } = {
  all_cash: "All-Cash",
  ppa: "PPA",
  lease: "Community Solar Lease",
  retail: "Retail",
  community_solar: "Community Solar Subscription",
};
const TECHNOLOGY_PREFERENCES: { [key: string]: string } = {
  carport_solar: "Carport Solar",
  rooftop_solar: "Rooftop Solar",
  ground_mounted_solar: "Ground-mounted Solar",
  battery_storage: "Battery",
  ev_charging: "EV Charging",
};
const GOALS: { [key: string]: string } = {
  clean_energy_leader: "Be a clean energy leader",
  energy_bill_reduction: "Reduce energy bill",
  ghg_emissions_reduction: "Reduce GHG emissions",
  energy_bill_predictability: "Energy bill predictability",
  energy_resiliency: "Energy resiliency",
  financial_benefit: "Financial benefit",
  select_new_provider: "Select a new solar energy provider",
};

export function useRfp(rfpId: MaybeRefOrGetter<string | undefined>) {
  const reactiveRfpId: Ref<string | undefined> = toRef(rfpId);
  const client = getClient();

  // Get the RFP
  const {
    state: rfp,
    isReady: ready,
    isLoading: loading,
    error: error,
    execute: reloadRfp,
  } = useAsyncState(
    async () => {
      if (reactiveRfpId.value && reactiveRfpId.value) {
        return await client
          .request<RfpCore>({
            url: `/rfps/${encodeURIComponent(reactiveRfpId.value)}`,
          })
          .then((t) => convertNullsToUndefined(t.data));
      } else {
        return;
      }
    },
    undefined,
    { resetOnExecute: false },
  );

  async function getAISummaries() {
    if (reactiveRfpId.value) {
      const res = await client.post<AIProposalSummaryAPIResponse[]>(
        `/rfps/${encodeURIComponent(
          reactiveRfpId.value,
        )}/generate_proposal_summaries`,
      );
      if (res.status !== 200) {
        throw new Error(`Generate AI summaries failed`);
      }
      const proposal_summaries: AIProposalSummary[] = [];
      for (const summary_record of Object.values(res.data)) {
        proposal_summaries.push({
          proposal_id: summary_record.proposal_id,
          provider_name: summary_record.provider_name,
          summary: summary_record.summary,
          created: new Date(summary_record.created),
          updated: new Date(summary_record.updated),
        });
      }
      return proposal_summaries;
    }
  }

  watch(reactiveRfpId, async () => await reloadRfp());

  const projectCount = computed(() => rfp.value?.project_count);
  const requestType = computed(() => (isRfq.value ? "RFQ" : "RFP"));
  const isRfq = computed(() => rfp.value?.is_rfq);
  const isPublicEntity = computed(() => rfp.value?.is_public_entity);

  return {
    rfp,
    projectCount,
    requestType,
    isRfq,
    isPublicEntity,
    reloadRfp,
    loading,
    ready,
    error,
    getAISummaries,
  };
}

export function useRfpPhases(rfpId: MaybeRefOrGetter<string | undefined>) {
  const reactiveRfpId: Ref<string | undefined> = toRef(rfpId);
  const client = getClient();

  // Get the RFP phases
  const {
    state: phaseResponse,
    isReady: ready,
    isLoading: loading,
    error: error,
    execute: reloadPhases,
  } = useAsyncState(
    async () => {
      if (reactiveRfpId.value && reactiveRfpId.value) {
        return await client
          .request<RfpPhaseResponse>({
            url: `/rfps/${encodeURIComponent(reactiveRfpId.value)}/phases`,
          })
          .then((t) => convertNullsToUndefined(t.data));
      } else {
        return;
      }
    },
    undefined,
    { resetOnExecute: false },
  );

  watch(reactiveRfpId, async () => await reloadPhases());

  const phases = computed(() => phaseResponse.value?.phases);
  const activePhases = computed(() => phaseResponse.value?.active_phases);

  const updateRfpPhases = async (rfpId: string, phases: RfpPhase[]) => {
    await client.request({
      method: "POST",
      url: `/rfps/${encodeURIComponent(rfpId)}/phases`,
      data: phases,
    });
    reloadPhases();
  };

  const preLaunchPeriod = computed(() => {
    return phases.value?.find(
      (phase: RfpPhase) => phase.phase_type === "pre_launch_period",
    );
  });
  const qaPeriod = computed(() => {
    return phases.value?.find(
      (phase: RfpPhase) => phase.phase_type === "qa_period",
    );
  });

  const firstRoundSubmissionPeriod = computed(() => {
    return phases.value?.find(
      (phase: RfpPhase) => phase.phase_type === "first_round_submission_period",
    );
  });

  const shortlistSubmissionPeriod = computed(() => {
    return phases.value?.find(
      (phase: RfpPhase) => phase.phase_type === "shortlist_submission_period",
    );
  });
  const firstRoundFixingPeriod = computed(() => {
    return phases.value?.find(
      (phase: RfpPhase) => phase.phase_type === "fixing_period",
    );
  });
  const shortlistFixingPeriod = computed(() => {
    return phases.value?.find(
      (phase: RfpPhase) => phase.phase_type === "shortlist_fixing_period",
    );
  });

  const rfpOpenDate = computed(() =>
    qaPeriod.value ? new Date(qaPeriod.value.start) : undefined,
  );
  const firstRoundOpenDate = computed(() =>
    firstRoundSubmissionPeriod.value
      ? new Date(firstRoundSubmissionPeriod.value.start)
      : undefined,
  );
  const firstRoundDeadline = computed(() =>
    firstRoundSubmissionPeriod.value
      ? new Date(firstRoundSubmissionPeriod.value.end)
      : undefined,
  );
  const shortlistDeadline = computed(() =>
    shortlistSubmissionPeriod.value
      ? new Date(shortlistSubmissionPeriod.value.end)
      : undefined,
  );

  const postSubmissionPeriod = computed(() => {
    const now = new Date();
    if (firstRoundDeadline.value) {
      return firstRoundDeadline.value < now;
    } else {
      return null;
    }
  });

  const postSubmissionNoShortlistSet = computed(() => {
    return (
      postSubmissionPeriod.value &&
      !phases.value.includes("shortlist_submission_period")
    );
  });

  const submissionPeriodInTheFuture = computed(() => {
    const now = new Date();
    return (
      firstRoundSubmissionPeriod.value &&
      firstRoundSubmissionPeriod.value.start > now
    );
  });

  const nextMilestone = computed(() => {
    const now = new Date();
    return phases.value?.find(
      (phase: RfpPhase) => phase.start !== undefined && phase.start > now,
    );
  });

  const inSubmissionPeriod = computed(() => {
    return activePhases.value?.includes("first_round_submission_period");
  });
  const inShortlistSubmissionPeriod = computed(() => {
    return activePhases.value?.includes("shortlist_submission_period");
  });
  const inFixingPeriod = computed(() => {
    return (
      activePhases.value?.includes("fixing_period") ||
      activePhases.value?.includes("shortlist_fixing_period")
    );
  });

  const fixingEndTime = computed(() => {
    if (activePhases.value?.includes("fixing_period")) {
      return firstRoundFixingPeriod.value?.end;
    }
    if (activePhases.value?.includes("shortlist_fixing_period")) {
      return shortlistFixingPeriod.value?.end;
    }
    return undefined;
  });

  return {
    phases,
    activePhases,
    updateRfpPhases,
    reloadPhases,
    inSubmissionPeriod,
    inShortlistSubmissionPeriod,
    postSubmissionPeriod,
    inFixingPeriod,
    fixingEndTime,
    nextMilestone,
    postSubmissionNoShortlistSet,
    submissionPeriodInTheFuture,
    preLaunchPeriod,
    qaPeriod,
    firstRoundSubmissionPeriod,
    shortlistSubmissionPeriod,
    firstRoundFixingPeriod,
    shortlistFixingPeriod,
    rfpOpenDate,
    firstRoundOpenDate,
    firstRoundDeadline,
    shortlistDeadline,
    loading,
    ready,
    error,
  };
}

export function useRfpRfis(rfpId: MaybeRefOrGetter<string | undefined>) {
  const reactiveRfpId: Ref<string | undefined> = toRef(rfpId);
  const client = getClient();

  // Get the RFIs
  const {
    state: rfis,
    isReady: ready,
    isLoading: loading,
    error: error,
    execute: reloadRfis,
  } = useAsyncState(
    async () => {
      if (reactiveRfpId.value && reactiveRfpId.value) {
        return await client
          .request<RFI[]>({
            url: `/rfps/${encodeURIComponent(reactiveRfpId.value)}/rfis`,
          })
          .then((t) => convertNullsToUndefined(t.data));
      } else {
        return;
      }
    },
    undefined,
    { resetOnExecute: false },
  );

  watch(reactiveRfpId, async () => await reloadRfis());

  return {
    rfis,
    reloadRfis,
    loading,
    ready,
    error,
  };
}

export function useRfpQas(rfpId: MaybeRefOrGetter<string | undefined>) {
  const reactiveRfpId: Ref<string | undefined> = toRef(rfpId);
  const client = getClient();

  // Get the Question & Answers
  const {
    state: qas,
    isReady: ready,
    isLoading: loading,
    error: error,
    execute: reloadQAs,
  } = useAsyncState(
    async () => {
      if (reactiveRfpId.value) {
        return await client
          .request<QA[]>({
            url: `/rfps/${encodeURIComponent(reactiveRfpId.value)}/qas`,
          })
          .then((t) => convertNullsToUndefined(t.data));
      } else {
        return;
      }
    },
    undefined,
    { resetOnExecute: false },
  );

  watch(reactiveRfpId, async () => await reloadQAs());

  return {
    qas,
    reloadQAs,
    loading,
    ready,
    error,
  };
}

export function useRfpLocations(rfpId: MaybeRefOrGetter<string | undefined>) {
  const reactiveRfpId: Ref<string | undefined> = toRef(rfpId);
  const client = getClient();

  // Get the RFP locations
  const {
    state: locations,
    isReady: ready,
    isLoading: loading,
    error: error,
    execute: reloadLocations,
  } = useAsyncState(
    async () => {
      if (reactiveRfpId.value) {
        return await client
          .request<RfpLocation[]>({
            url: `/rfps/${encodeURIComponent(reactiveRfpId.value)}/locations`,
          })
          .then((t) => convertNullsToUndefined(t.data));
      } else {
        return;
      }
    },
    undefined,
    { resetOnExecute: false },
  );

  watch(reactiveRfpId, async () => await reloadLocations());

  function getLocationById(locationId: string | undefined) {
    // NOTE: In practice, locationId should never be undefined (we just can't use Typescript non-undefined
    // assertions in Vue templates)
    if (!locationId) return;

    return locations.value?.find(
      (loc: RfpLocation) => loc.data.id === locationId,
    );
  }

  return {
    locations,
    reloadLocations,
    loading,
    ready,
    error,
    getLocationById,
  };
}

export function useRfpLocation(
  rfpId: MaybeRefOrGetter<string | undefined>,
  locationId: MaybeRefOrGetter<string | undefined>,
) {
  const reactiveRfpId: Ref<string | undefined> = toRef(rfpId);
  const reactiveLocationId: Ref<string | undefined> = toRef(locationId);

  const client = getClient();

  const {
    state: location,
    isReady: ready,
    isLoading: loading,
    error: error,
    execute: reloadLocation,
  } = useAsyncState(
    async () => {
      if (reactiveRfpId.value && reactiveLocationId.value) {
        return await client
          .request<Location>({
            url: `/rfps/${encodeURIComponent(
              reactiveRfpId.value,
            )}/locations/${encodeURIComponent(reactiveLocationId.value)}`,
          })
          .then((t) => convertNullsToUndefined(t.data));
      } else {
        return;
      }
    },
    undefined,
    { resetOnExecute: false },
  );

  // Use this for the shortest possible address to show in a chip/button
  const shortestAddress = computed(() => {
    if (!reactiveLocationId.value || !location.value) return;
    if (location.value.address.street_address)
      return location.value.address.street_address;
    // Formatted address is required and will always exist
    return location.value.address.formatted;
  });

  const formatted = computed(() => {
    if (!reactiveLocationId.value || !location.value) return;
    return location.value.address.formatted || null;
  });

  watch(
    [reactiveRfpId, reactiveLocationId],
    async () => await reloadLocation(),
  );

  return {
    location,
    shortestAddress,
    formatted,
    reloadLocation,
    loading,
    ready,
    error,
  };
}

export function useRfpDisplayProperties(rfp: Ref<RfpCore | undefined>) {
  const project = computed(() => rfp.value?.project);

  const contactEmail = computed(() => {
    return rfp.value ? `${rfp.value.id}@stationa.com` : null;
  });

  const displayFinancingPreferences = computed(() => {
    if (!project.value || !project.value.financingPreferences) return [];
    return project.value.financingPreferences.map(
      (fp: string) => FINANCING_PREFERENCES[fp],
    );
  });

  const displayTechnologyPreferences = computed(() => {
    if (!project.value || !project.value.technologyPreferences) return [];
    return project.value.technologyPreferences.map(
      (tp: string) => TECHNOLOGY_PREFERENCES[tp],
    );
  });

  const displayGoals = computed(() => {
    if (!project.value || !project.value.goals) return [];
    return project.value.goals.map((tp: string) => GOALS[tp]);
  });

  const peakAndUsageMessage = computed(() => {
    if (!project.value) return "";
    if (project.value.annualUsageKwh) {
      return project.value.annualUsageKwh
        ? "Maximum Annual Peak / Total Annual Usage"
        : "Maximum Annual Peak";
    } else {
      return project.value!.annualUsageKwh ? "Total Annual Usage" : null;
    }
  });

  const peakAndUsageValue = computed(() => {
    if (!project.value) return "";
    const annualPeak = project.value.annualPeakKw
      ? aggValue(project.value.annualPeakKw, "kW")
      : "";
    const annualUsage = project.value.annualUsageKwh
      ? aggValue(project.value.annualUsageKwh, "kWh")
      : "";

    if (!!annualPeak && !!annualUsage) {
      // Both peak and usage are specified.
      return `${annualPeak} / ${annualUsage}`;
    }
    if (!!annualPeak != !!annualUsage) {
      // Only one is specified. The other one is an empty string.
      return [annualPeak, annualUsage].join("");
    }
    // None is specified if we end here.
    return null;
  });

  const rfpSummaryCardItems = computed(() => {
    if (!project.value) return [];
    const items: Array<{
      icon: string;
      value: string | null;
      subtitle: string;
      mailtoContact?: string;
    }> = [
      {
        icon: "bolt",
        value: peakAndUsageValue.value,
        subtitle: peakAndUsageMessage.value!,
      },
      {
        icon: "power",
        value: project.value.costOfElectricity
          ? aggValue(project.value.costOfElectricity!.toFixed(2), "/kWh")
          : null,
        subtitle: "Average Cost of Electricity",
      },
    ];

    if (displayFinancingPreferences.value.length > 0) {
      items.push({
        icon: "account_balance_wallet",
        value: displayFinancingPreferences.value!.join(", "),
        subtitle: "Financing",
      });
    }

    if (displayTechnologyPreferences.value.length > 0) {
      items.push({
        icon: "build",
        value: displayTechnologyPreferences.value!.join(", "),
        subtitle: "Technology",
      });
    }

    items.push({
      icon: "account_circle",
      value: `${rfp.value!.manager!.first_name} ${
        rfp.value!.manager!.last_name
      }`,
      subtitle: "Marketplace Success Rep",
      mailtoContact: contactEmail.value!,
    });

    return items;
  });

  const renderedIntroStatement = computed(() => {
    if (!rfp.value || !rfp.value.intro_statement) return "";

    const { blockHTML } = useMD(rfp.value.intro_statement);
    return blockHTML.value;
  });

  return {
    project,
    contactEmail,
    rfpSummaryCardItems,
    renderedIntroStatement,
    displayGoals,
  };
}
